import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import Seo from "@/components/Seo";
import DispatchCountdown from "@/components/DispatchCountdown";
import ReviewsSection, { StarRating } from "@/components/ReviewsSection";
import { toast } from "sonner";

export default function Product() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [bac, setBac] = useState(null);
  const [addBundle, setAddBundle] = useState(true);
  const [qty, setQty] = useState(1);
  const [showSticky, setShowSticky] = useState(false);
  const [reviews, setReviews] = useState({ reviews: [], count: 0, avg: 0 });
  const navigate = useNavigate();

  const loadReviews = useCallback(() => {
    api.get(`/products/${slug}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => {});
    api.get(`/products/bac-water-10ml`).then((r) => setBac(r.data)).catch(() => {});
    loadReviews();
  }, [slug, loadReviews]);

  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 500);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!p) return <div className="pt-40 text-center font-mono-label text-xs">LOADING…</div>;

  const isPeptide = p.category === "peptide";
  const savings = bac && isPeptide ? bac.price * 0.20 : 0;
  // Bulk discount: 3-4 units = 10%, 5+ = 15%
  const bulkPct = qty >= 5 ? 15 : qty >= 3 ? 10 : 0;
  const effUnit = p.price * (1 - bulkPct / 100);
  const bundlePrice = effUnit * qty + (addBundle && isPeptide && bac ? bac.price - savings : 0);

  const nl = lang === "nl";
  const de = lang === "de";
  const doAdd = () => {
    add(p, qty);
    if (isPeptide && bac && addBundle) {
      add(bac, 1);
      toast.success(nl ? `${p.name} + BAC Water toegevoegd` : de ? `${p.name} + BAC Wasser hinzugefügt` : `${p.name} + BAC Water added`);
    } else {
      toast.success(nl ? `${p.name} toegevoegd` : de ? `${p.name} hinzugefügt` : `${p.name} added`);
    }
  };
  const addAndGo = () => { doAdd(); navigate("/cart"); };

  const stockLevel =
    p.stock <= 5
      ? { label: nl ? `Nog maar ${p.stock} op voorraad` : de ? `Nur noch ${p.stock} auf Lager` : `Only ${p.stock} left in stock`, color: "#D12A2F", pulse: true }
      : p.stock <= 20
      ? { label: nl ? `Nog ${p.stock} op voorraad` : de ? `Noch ${p.stock} auf Lager` : `${p.stock} in stock`, color: "#D97706", pulse: false }
      : { label: nl ? "Op voorraad · verzend vandaag" : de ? "Auf Lager · heute versandt" : "In stock · ships today", color: "#059669", pulse: false };

  return (
    <div data-testid="product-page" className="pt-28 pb-24 bg-[#F7F7F6]">
      <Seo
        title={`${p.name} · ${p.dosage} — Peptivance`}
        description={p.short_desc_en || p.short_desc_nl || `Research grade ${p.name} ${p.dosage}. Lyophilized, HPLC validated. For in-vitro research only.`}
        url={`https://peptivance.eu/product/${p.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: `${p.name} ${p.dosage}`,
          description: p.short_desc_en || p.short_desc_nl,
          image: p.image_url,
          brand: { "@type": "Brand", name: "Peptivance" },
          sku: p.slug,
          category: p.category === "bac_water" ? "Solvent" : "Peptide",
          offers: {
            "@type": "Offer",
            priceCurrency: "EUR",
            price: Number(p.price).toFixed(2),
            availability: p.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            url: `https://peptivance.eu/product/${p.slug}`,
          },
        }}
      />

      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="font-mono-label text-[11px] text-neutral-500 mb-8">
          <Link to="/shop" className="hover:underline">CATALOGUE</Link> / {p.name.toUpperCase()}
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="relative bg-white border border-neutral-300 overflow-hidden aspect-square">
            <div className="absolute inset-0 spotlight" />
            <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(circle at center, ${p.accent}22, transparent 55%)`, filter: "blur(30px)" }} />
            <motion.img
              src={p.image_url}
              alt={p.name}
              className="absolute inset-0 w-full h-full object-contain p-10 z-10"
              style={{ filter: `drop-shadow(0 40px 60px ${p.accent}55)` }}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            />
            <div className="absolute top-6 left-6 font-mono-label text-[10px] text-neutral-500 z-20">[ SKU · {p.slug.toUpperCase()} ]</div>
            <div className="absolute bottom-6 right-6 font-mono-label text-[10px] z-20" style={{ color: p.accent }}>· {p.dosage}</div>
          </div>

          <div className="md:pl-8">
            <p className="font-mono-label text-[11px] mb-6" style={{ color: p.accent }}>
              [ {p.category === "bac_water" ? "SOLVENT" : p.category === "peptide_pen" ? "PEN" : p.category === "accessory" ? "KIT" : "PEPTIDE"} ]
            </p>
            <h1 className="font-serif-display font-light text-5xl md:text-7xl leading-[0.95] tracking-tight">
              <MaskedLine>{p.name}</MaskedLine>
              <MaskedLine i={1} className="italic text-neutral-500">{p.dosage}</MaskedLine>
            </h1>

            {/* Trust ribbon */}
            <FadeUp delay={0.2}>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <span
                  className="inline-flex items-center gap-2 border px-3 py-1.5 bg-white"
                  style={{ borderColor: `${stockLevel.color}55` }}
                  data-testid="stock-badge"
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${stockLevel.pulse ? "animate-pulse" : ""}`}
                    style={{ background: stockLevel.color }}
                  />
                  <span className="font-mono-label text-[10px]" style={{ color: stockLevel.color }}>
                    {stockLevel.label}
                  </span>
                </span>
                <DispatchCountdown variant="default" />
                {reviews.count > 0 && (
                  <StarRating rating={reviews.avg} count={reviews.count} accent={p.accent} />
                )}
              </div>
            </FadeUp>

            <FadeUp delay={0.3}>
              <p className="mt-8 text-neutral-600 text-lg leading-relaxed max-w-lg">
                {lang === "nl" ? p.long_desc_nl : p.long_desc_en}
              </p>
            </FadeUp>

            <div className="mt-10 flex items-end justify-between border-b border-neutral-300 pb-6">
              <div>
                <p className="font-mono-label text-[10px] text-neutral-500">{nl ? "Prijs" : de ? "Preis" : "Price"}</p>
                <p className="font-serif-display text-5xl">
                  €{Number(effUnit).toFixed(2)}
                  {bulkPct > 0 && (
                    <span className="ml-3 font-mono-label text-[11px] text-neutral-400 line-through">
                      €{Number(p.price).toFixed(2)}
                    </span>
                  )}
                </p>
                {p.category !== "accessory" && (
                  <p className="font-mono-label text-[10px] text-neutral-500 mt-1">
                    ~€{(effUnit / (parseFloat(p.dosage) || 1)).toFixed(2)} per {p.category === "bac_water" ? "ml" : "mg"}
                    {bulkPct > 0 && (
                      <span className="ml-2 text-emerald-700">
                        ✓ {bulkPct}% {nl ? "bulkkorting actief" : de ? "Mengenrabatt aktiv" : "bulk discount active"}
                      </span>
                    )}
                  </p>
                )}
              </div>
              <div className="flex items-center border border-black shrink-0" data-testid="qty-picker">
                <button data-testid="qty-dec" type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="min-w-[48px] min-h-[48px] px-4 py-3 font-mono-label text-base active:bg-black active:text-[#F7F7F6] hover:bg-black hover:text-[#F7F7F6] transition-colors select-none touch-manipulation">−</button>
                <span data-testid="qty-val" className="px-5 min-w-[44px] text-center font-mono-label text-sm select-none">{qty}</span>
                <button data-testid="qty-inc" type="button" onClick={() => setQty((q) => q + 1)} className="min-w-[48px] min-h-[48px] px-4 py-3 font-mono-label text-base active:bg-black active:text-[#F7F7F6] hover:bg-black hover:text-[#F7F7F6] transition-colors select-none touch-manipulation">+</button>
              </div>
            </div>

            {/* Bulk pricing ladder — prominent visual tier selector */}
            <FadeUp delay={0.32}>
              <div className="mt-6">
                <div className="flex items-baseline justify-between mb-3">
                  <p className="font-mono-label text-[11px] tracking-[0.2em]" style={{ color: p.accent }}>
                    [ {nl ? "STAFFELKORTING" : de ? "MENGENRABATT" : "BULK DISCOUNT"} ]
                  </p>
                  {bulkPct > 0 && (
                    <span
                      className="font-mono-label text-[10px] px-2 py-0.5 text-white animate-pulse"
                      style={{ background: p.accent }}
                    >
                      {nl ? `JE BESPAART €${((p.price - effUnit) * qty).toFixed(2)}` : de ? `SPAREN €${((p.price - effUnit) * qty).toFixed(2)}` : `YOU SAVE €${((p.price - effUnit) * qty).toFixed(2)}`}
                    </span>
                  )}
                </div>
                <div data-testid="bulk-pricing" className="grid grid-cols-3 gap-2">
                  {[
                    { min: 1, max: 2, pct: 0, label: "1–2", labelSub: nl ? "eenheden" : de ? "Stück" : "units" },
                    { min: 3, max: 4, pct: 10, label: "3–4", labelSub: nl ? "eenheden" : de ? "Stück" : "units" },
                    { min: 5, max: 999, pct: 15, label: "5+", labelSub: nl ? "eenheden" : de ? "Stück" : "units" },
                  ].map((tier) => {
                    const active = qty >= tier.min && qty <= tier.max;
                    const price = p.price * (1 - tier.pct / 100);
                    const save = (p.price - price) * tier.min;
                    return (
                      <button
                        key={tier.min}
                        type="button"
                        onClick={() => setQty(tier.min)}
                        data-testid={`bulk-tier-${tier.min}`}
                        className={`relative border-2 p-3 md:p-4 text-center transition-all touch-manipulation ${
                          active
                            ? "shadow-lg scale-[1.03]"
                            : "border-neutral-300 bg-white/60 hover:border-neutral-500"
                        }`}
                        style={active ? { borderColor: p.accent, background: `${p.accent}0d` } : {}}
                      >
                        {tier.pct > 0 && (
                          <span
                            className="absolute -top-2.5 left-1/2 -translate-x-1/2 font-mono-label text-[9px] px-2 py-0.5 text-white whitespace-nowrap"
                            style={{ background: p.accent }}
                          >
                            −{tier.pct}%
                          </span>
                        )}
                        <p className="font-serif-display text-2xl md:text-3xl leading-none">{tier.label}</p>
                        <p className="font-mono-label text-[8px] md:text-[9px] text-neutral-500 mt-0.5">{tier.labelSub}</p>
                        <p className="font-serif-display text-base md:text-lg mt-2" style={active ? { color: p.accent } : {}}>
                          €{price.toFixed(2)}
                        </p>
                        <p className="font-mono-label text-[8px] md:text-[9px] mt-1" style={{ color: tier.pct > 0 ? p.accent : "#888" }}>
                          {tier.pct > 0 ? (nl ? `Bespaar €${save.toFixed(0)}` : de ? `Spar €${save.toFixed(0)}` : `Save €${save.toFixed(0)}`) : (nl ? "Basisprijs" : de ? "Grundpreis" : "Regular")}
                        </p>
                      </button>
                    );
                  })}
                </div>
                {qty < 3 && (
                  <p className="mt-3 font-mono-label text-[10px] text-neutral-600 text-center">
                    {nl
                      ? `↑ Klik een staffel of voeg ${3 - qty} extra toe voor 10% korting`
                      : de
                      ? `↑ Klicken Sie eine Stufe oder fügen Sie ${3 - qty} weitere hinzu für 10% Rabatt`
                      : `↑ Click a tier or add ${3 - qty} more for 10% off`}
                  </p>
                )}
              </div>
            </FadeUp>

            {/* BAC Water bundle upsell */}
            {isPeptide && bac && (
              <FadeUp delay={0.35}>
                <label
                  data-testid="bundle-upsell"
                  className={`mt-6 flex items-center gap-4 border p-4 cursor-pointer transition-colors ${addBundle ? "border-black bg-white" : "border-neutral-300 bg-white/60"}`}
                >
                  <input
                    type="checkbox"
                    checked={addBundle}
                    onChange={(e) => setAddBundle(e.target.checked)}
                    className="w-4 h-4 accent-black"
                  />
                  <img src={bac.image_url} alt="BAC Water" className="w-10 h-10 object-contain" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono-label text-[9px] text-white bg-[#00529B] px-1.5 py-0.5">BUNDLE · SAVE 20%</span>
                      <span className="font-mono-label text-[10px] text-neutral-500">
                        {nl ? "Nodig voor reconstitutie" : de ? "Zur Rekonstitution benötigt" : "Required for reconstitution"}
                      </span>
                    </div>
                    <p className="mt-1 text-sm">
                      <span className="font-semibold">
                        {nl ? "Voeg BAC Water 10ml toe" : de ? "BAC Wasser 10ml hinzufügen" : "Add BAC Water 10ml"}
                      </span>
                      <span className="text-neutral-500">
                        {nl
                          ? ` — noodzakelijk om ${p.name} op te lossen`
                          : de
                          ? ` — nötig um ${p.name} aufzulösen`
                          : ` — required to reconstitute ${p.name}`}
                      </span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono-label text-[10px] text-neutral-400 line-through">€{bac.price.toFixed(2)}</p>
                    <p className="font-serif-display text-xl">€{(bac.price - savings).toFixed(2)}</p>
                  </div>
                </label>
              </FadeUp>
            )}

            <FadeUp delay={0.4}>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button data-testid="product-add-cart" onClick={doAdd} className="btn-sharp">
                  {t("add_to_cart")}
                </button>
                <button data-testid="product-buy-now" onClick={addAndGo} className="btn-solid">
                  {t("cart_checkout")} · €{bundlePrice.toFixed(2)} →
                </button>
              </div>
            </FadeUp>

            <FadeUp delay={0.5}>
              <div className="mt-10 grid grid-cols-2 gap-4 font-mono-label text-[10px] text-neutral-500">
                <div className="border-t border-neutral-300 pt-3">[ ≥99% ] Purity by HPLC</div>
                <div className="border-t border-neutral-300 pt-3">[ ISO 5 ] Aseptic fill</div>
                <div className="border-t border-neutral-300 pt-3">[ 24h ] Dispatch NL</div>
                <div className="border-t border-neutral-300 pt-3">[ COA ] Available on request</div>
              </div>
            </FadeUp>

            <FadeUp delay={0.55}>
              <div className="mt-10 border border-neutral-300 p-6 bg-white">
                <p className="font-mono-label text-[10px] mb-2" style={{ color: p.accent }}>[ DISCLAIMER ]</p>
                <p className="text-neutral-700 text-sm leading-relaxed">
                  {t("legal_disclaimer")}
                </p>
              </div>
            </FadeUp>
          </div>
        </div>

        {/* Reviews */}
        <ReviewsSection
          productSlug={p.slug}
          productName={p.name}
          accent={p.accent}
          reviews={reviews.reviews}
          avg={reviews.avg}
          count={reviews.count}
          onReviewAdded={loadReviews}
        />
      </div>

      {/* Sticky add-to-cart bar */}
      <AnimatePresence>
        {showSticky && (
          <motion.div
            data-testid="sticky-atc"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-neutral-300 shadow-2xl"
          >
            <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-3 flex items-center gap-4">
              <img src={p.image_url} alt="" className="h-12 w-12 object-contain" />
              <div className="flex-1 min-w-0">
                <p className="font-serif-display text-lg leading-none truncate">{p.name}</p>
                <p className="font-mono-label text-[10px] text-neutral-500 mt-1">{p.dosage} · €{bundlePrice.toFixed(2)}</p>
              </div>
              <div className="hidden md:block">
                <DispatchCountdown variant="compact" />
              </div>
              <div className="flex items-center border border-black shrink-0" data-testid="sticky-qty-picker">
                <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="min-w-[40px] min-h-[40px] px-3 py-2 font-mono-label text-base active:bg-black active:text-[#F7F7F6] hover:bg-black hover:text-[#F7F7F6] transition-colors select-none touch-manipulation">−</button>
                <span className="px-3 min-w-[32px] text-center font-mono-label text-sm select-none">{qty}</span>
                <button type="button" onClick={() => setQty((q) => q + 1)} className="min-w-[40px] min-h-[40px] px-3 py-2 font-mono-label text-base active:bg-black active:text-[#F7F7F6] hover:bg-black hover:text-[#F7F7F6] transition-colors select-none touch-manipulation">+</button>
              </div>
              <button onClick={addAndGo} className="btn-solid whitespace-nowrap">
                {t("cart_checkout")} →
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
